const TSUNAMI_WARNING_RANKS = {
    '': 0,
    '津波注意報': 1,
    '津波警報': 2,
    '大津波警報': 3
};

const TSUNAMI_LEVEL_COLORS = {
    0: '#5c7187',
    1: '#7bb7e8',
    2: '#fff200',
    3: '#ff1744',
    4: '#8e24aa'
};

const TSUNAMI_LEVEL_FILL_COLORS = {
    0: '#5c7187',
    1: '#7bb7e8',
    2: '#fff200',
    3: '#ff1744',
    4: '#8e24aa'
};

const TSUNAMI_WARNING_BORDER_COLORS = {
    0: 'rgba(255, 255, 255, 0.92)',
    1: '#fff200',
    2: '#ff1744',
    3: '#8e24aa'
};

function getTsunamiWarningTypeRankFromMarkerLevel(level) {
    const numericLevel = Number(level);

    if (numericLevel >= 4) {
        return 3;
    }
    if (numericLevel === 3) {
        return 2;
    }
    if (numericLevel === 2) {
        return 1;
    }
    return 0;
}

function parseTsunamiDate(value) {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

export function escapeHTML(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function getTsunamiWarningTypeRank(value) {
    return TSUNAMI_WARNING_RANKS[String(value ?? '').trim()] || 0;
}

export function getTsunamiWarningTypeLabel(value, markerLevel = 0) {
    const normalizedValue = String(value ?? '').trim();

    if (normalizedValue) {
        return normalizedValue;
    }
    if (markerLevel >= 4) {
        return '大津波警報';
    }
    if (markerLevel === 3) {
        return '津波警報';
    }
    if (markerLevel === 2) {
        return '津波注意報';
    }
    return '津波情報';
}

export function getTsunamiMarkerLevel(record = {}) {
    const rawMarkerLevel = Number(record.markerLevel);

    if (Number.isFinite(rawMarkerLevel)) {
        return Math.max(0, Math.min(4, Math.trunc(rawMarkerLevel)));
    }

    const heightValue = Number(record.heightValue ?? record.height);

    if (Number.isFinite(heightValue)) {
        if (heightValue >= 3) {
            return 4;
        }
        if (heightValue >= 1) {
            return 3;
        }
        if (heightValue >= 0.2) {
            return 2;
        }
        return 1;
    }

    return getTsunamiWarningTypeRank(record.wavetypename) + 1;
}

export function getTsunamiMarkerWarningRank(level) {
    return getTsunamiWarningTypeRankFromMarkerLevel(level);
}

export function getTsunamiMarkerColor(level) {
    return TSUNAMI_LEVEL_COLORS[Number(level)] || TSUNAMI_LEVEL_COLORS[0];
}

export function getTsunamiMarkerFillColor(level) {
    return TSUNAMI_LEVEL_FILL_COLORS[Number(level)] || TSUNAMI_LEVEL_FILL_COLORS[0];
}

export function getTsunamiWarningBorderColor(value) {
    const numericValue = Number(value),
        warningRank = Number.isFinite(numericValue) ? numericValue : getTsunamiWarningTypeRank(value);

    return TSUNAMI_WARNING_BORDER_COLORS[warningRank] || TSUNAMI_WARNING_BORDER_COLORS[0];
}

export function mergeTsunamiStationsByLatestObservation(stations = []) {
    const mergedStations = new Map();

    stations
        .filter(station => station && typeof station === 'object')
        .forEach(station => {
            const key = String(
                station.stationcode ||
                station.stationname ||
                `${station.longitude || ''}:${station.latitude || ''}`
            );
            const current = mergedStations.get(key);

            if (!current) {
                mergedStations.set(key, station);
                return;
            }

            const currentObserveTime = parseTsunamiDate(current.observetime),
                nextObserveTime = parseTsunamiDate(station.observetime);

            if (currentObserveTime && nextObserveTime && currentObserveTime.getTime() !== nextObserveTime.getTime()) {
                if (nextObserveTime > currentObserveTime) {
                    mergedStations.set(key, station);
                }
                return;
            }
            if (!currentObserveTime && nextObserveTime) {
                mergedStations.set(key, station);
                return;
            }

            const currentReportTime = parseTsunamiDate(current.report_datetime),
                nextReportTime = parseTsunamiDate(station.report_datetime);

            if (currentReportTime && nextReportTime && currentReportTime.getTime() !== nextReportTime.getTime()) {
                if (nextReportTime > currentReportTime) {
                    mergedStations.set(key, station);
                }
                return;
            }
            if (!currentReportTime && nextReportTime) {
                mergedStations.set(key, station);
                return;
            }

            const currentMarkerLevel = Number(current.markerLevel) || 0,
                nextMarkerLevel = Number(station.markerLevel) || 0;

            if (nextMarkerLevel !== currentMarkerLevel) {
                if (nextMarkerLevel > currentMarkerLevel) {
                    mergedStations.set(key, station);
                }
                return;
            }

            const currentHeightValue = Number.isFinite(current.heightValue) ? current.heightValue : -1,
                nextHeightValue = Number.isFinite(station.heightValue) ? station.heightValue : -1;

            if (nextHeightValue > currentHeightValue) {
                mergedStations.set(key, station);
            }
        });

    return Array.from(mergedStations.values()).sort((a, b) => {
        const aObserveTime = parseTsunamiDate(a.observetime),
            bObserveTime = parseTsunamiDate(b.observetime),
            aHeightValue = Number.isFinite(a.heightValue) ? a.heightValue : -1,
            bHeightValue = Number.isFinite(b.heightValue) ? b.heightValue : -1;

        if ((Number(b.markerLevel) || 0) !== (Number(a.markerLevel) || 0)) {
            return (Number(b.markerLevel) || 0) - (Number(a.markerLevel) || 0);
        }
        if (aObserveTime && bObserveTime && aObserveTime.getTime() !== bObserveTime.getTime()) {
            return bObserveTime - aObserveTime;
        }
        if (bObserveTime) {
            return 1;
        }
        if (aObserveTime) {
            return -1;
        }
        if (bHeightValue !== aHeightValue) {
            return bHeightValue - aHeightValue;
        }
        return String(a.stationname || '').localeCompare(String(b.stationname || ''), 'ja');
    });
}

export function formatTsunamiDateTime(value, withSeconds = true) {
    if (!value) {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    const year = date.getFullYear(),
        month = `0${date.getMonth() + 1}`.slice(-2),
        day = `0${date.getDate()}`.slice(-2),
        hours = `0${date.getHours()}`.slice(-2),
        minutes = `0${date.getMinutes()}`.slice(-2),
        seconds = `0${date.getSeconds()}`.slice(-2);

    return withSeconds ?
        `${year}/${month}/${day} ${hours}:${minutes}:${seconds}` :
        `${year}/${month}/${day} ${hours}:${minutes}`;
}

export function buildTsunamiButtonSummary(tsunami) {
    if (!tsunami) {
        return '';
    }

    return [
        formatTsunamiDateTime(tsunami.report_datetime, false),
        tsunami.warningType || '',
        Number.isFinite(tsunami.targetAreaCount) ? `${tsunami.targetAreaCount}地域` : ''
    ].filter(Boolean).join(' ');
}
